"use client";

import { useEffect, useId, useRef, useState } from "react";
import Script from "next/script";
import { CheckCircleIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { TrackingEvents } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";
import {
  buildBrowserAttributionEnvelope,
  createSubmissionId,
  setBrowserAttributionLeadId,
} from "@/lib/ad-attribution";
import { useTurnstile } from "@/hooks/useTurnstile";
import { submitCrmForm } from "@/lib/form-submission";
import { FieldLabel, InlineError, fieldClassName } from "@/components/shared/DesignSystem";

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "pk.eyJ1IjoidGVkY2FvY29tbyIsImEiOiJjbTgxeW0wOGYwbWdsMmlwenVpY2x0d244In0.0EI4_0iJRISomZ78wOOS5Q";

const APPLICATIONS = [
  ["", "Select the main application"],
  ["drinking-water", "Drinking water for staff or customers"],
  ["equipment-protection", "Scale control or equipment protection"],
  ["process-water", "Process or production water"],
  ["source-water-safety", "Source-water safety or disinfection"],
  ["automation-controls", "PLC, controls, or system integration"],
  ["existing-system-service", "Existing system service or replacement"],
  ["not-sure", "Not sure — help us scope it"],
];

const FACILITY_TYPES = [
  ["", "Select facility type"],
  ["restaurant-hospitality", "Restaurant or hospitality"],
  ["office-retail", "Office or retail"],
  ["healthcare-lab", "Healthcare or laboratory"],
  ["manufacturing-processing", "Manufacturing or processing"],
  ["multi-residential", "Multi-residential"],
  ["agriculture-rural", "Agriculture or rural operation"],
  ["other", "Other"],
];

const INITIAL_DATA = {
  companyName: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  application: "",
  facilityType: "",
  targetFlow: "",
  projectDetails: "",
  address: "",
  consultationWindow: "",
};

export function BusinessAssessmentForm() {
  const id = useId();
  const formRef = useRef(null);
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const addressTimerRef = useRef(null);
  const addressAbortRef = useRef(null);
  const submissionIdRef = useRef(null);
  const submittingRef = useRef(false);
  const [formData, setFormData] = useState(INITIAL_DATA);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const { turnstileRef, turnstileToken, resetTurnstile, handleScriptLoad } = useTurnstile();
  const trackingDimensions = { form_type: "business_assessment", form_variant: "business-assessment-general", placement: "inline", cta_variant: "business-assessment", page_path: "/forms/business-assessment" };
  const trackStart = useFormTracking({ formRef, dimensions: trackingDimensions });

  const turnstileError = turnstileToken ? null : errors.turnstile;

  useEffect(() => {
    function handleOutside(event) {
      if (!suggestionsRef.current?.contains(event.target) && !addressInputRef.current?.contains(event.target)) {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
    }
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  useEffect(() => () => {
    clearTimeout(addressTimerRef.current);
    addressAbortRef.current?.abort();
  }, []);

  function clearFieldError(name) {
    if (!errors[name]) return;
    setErrors((current) => {
      const next = { ...current };
      delete next[name];
      return next;
    });
  }

  function handleChange(event) {
    const { name, value } = event.target;
    clearFieldError(name);
    setFormData((current) => ({ ...current, [name]: value }));
  }

  function selectSuggestion(suggestion) {
    const address = suggestion.properties?.full_address || suggestion.place_name || suggestion.properties?.name || "";
    setFormData((current) => ({ ...current, address }));
    setShowSuggestions(false);
    setActiveSuggestion(-1);
    requestAnimationFrame(() => addressInputRef.current?.focus());
  }

  function handleAddressKeyDown(event) {
    if (!showSuggestions || suggestions.length === 0) {
      if (event.key === "Escape") {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestion((current) => (current + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestion((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
    } else if (event.key === "Enter" && activeSuggestion >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeSuggestion]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  }

  function handleAddressChange(event) {
    const value = event.target.value;
    setFormData((current) => ({ ...current, address: value }));
    setActiveSuggestion(-1);
    clearTimeout(addressTimerRef.current);
    addressAbortRef.current?.abort();

    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
      return;
    }

    setShowSuggestions(true);
    setIsLoadingSuggestions(true);
    addressTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      addressAbortRef.current = controller;
      try {
        const endpoint = `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(value.trim())}&access_token=${MAPBOX_TOKEN}&country=ca&limit=5&proximity=ip`;
        const response = await fetch(endpoint, { signal: controller.signal });
        if (!response.ok) throw new Error(`Address lookup failed with ${response.status}`);
        const data = await response.json();
        setSuggestions(Array.isArray(data.features) ? data.features : []);
      } catch (error) {
        if (error?.name !== "AbortError") setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setIsLoadingSuggestions(false);
      }
    }, 300);
  }

  function validate() {
    const nextErrors = {};
    if (!formData.companyName.trim()) nextErrors.companyName = "Enter your company name.";
    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    const digits = formData.phone.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 15) nextErrors.phone = "Enter a valid phone number with 10 to 15 digits.";
    if (!formData.application) nextErrors.application = "Select the main application.";
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!turnstileToken) nextErrors.turnstile = "Complete the human verification check.";
    return nextErrors;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    trackStart();

    const nextErrors = validate();
    TrackingEvents.trackFormStep("submit_attempt", { ...trackingDimensions, application: formData.application || "unset" });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus({ type: "error", message: "Please check the highlighted fields and try again." });
      TrackingEvents.trackFormStep("validation_error", { ...trackingDimensions, error_fields: Object.keys(nextErrors).join(","), error_count: Object.keys(nextErrors).length });
      requestAnimationFrame(() => formRef.current?.querySelector("[aria-invalid='true']")?.focus());
      return;
    }

    submittingRef.current = true;
    try {
      setIsSubmitting(true);
      setStatus({ type: "idle", message: "" });
      submissionIdRef.current ||= createSubmissionId();
      const attribution = buildBrowserAttributionEnvelope({
        submissionId: submissionIdRef.current,
        formType: "business_assessment",
      });
      const { response, result } = await submitCrmForm("/api/crm/submit-assessment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "new",
          type: "business-assessment",
          company_name: formData.companyName,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          application: formData.application,
          facility_type: formData.facilityType,
          target_flow: formData.targetFlow,
          project_details: formData.projectDetails,
          address: formData.address,
          consultation_window: formData.consultationWindow,
          program_interest: formData.application === "automation-controls" ? "plc-industrial" : "business-water",
          source: attribution?.submissionTouch?.source,
          attribution,
          consultation_context: {
            mode: "business",
            audience: "business",
            service: formData.application === "automation-controls" ? "plc-industrial" : "business-water",
            pagePath: "/forms/business-assessment",
            placement: "inline",
            ctaVariant: `business-${formData.application}`,
          },
          turnstile_token: turnstileToken,
        }),
      });
      if (!response.ok || !result.success) {
        if (result.fieldErrors) {
          setErrors((current) => ({
            ...current,
            ...(result.fieldErrors.company_name ? { companyName: result.fieldErrors.company_name } : {}),
            ...(result.fieldErrors.first_name ? { firstName: result.fieldErrors.first_name } : {}),
            ...(result.fieldErrors.phone ? { phone: result.fieldErrors.phone } : {}),
            ...(result.fieldErrors.email ? { email: result.fieldErrors.email } : {}),
            ...(result.fieldErrors.application ? { application: result.fieldErrors.application } : {}),
          }));
        }
        const submissionError = new Error(result.error || "Your business request could not be submitted.");
        submissionError.code = result.code;
        throw submissionError;
      }

      const leadId = result.data?.leadId || result.data?.project?.projectId || result.data?.project?.id;
      if (!leadId) throw new Error("Lead creation was not confirmed.");
      try { setBrowserAttributionLeadId(leadId); }
      catch (storageError) { console.error("Confirmed lead storage failed:", storageError); }
      try {
        TrackingEvents.trackBusinessAssessment({ formId: leadId, ...trackingDimensions, application: formData.application });
      } catch (trackingError) { console.error("Confirmed lead tracking failed:", trackingError); }
      submissionIdRef.current = null;
      setFormData(INITIAL_DATA);
      setErrors({});
      setStatus({
        type: "success",
        message: "Our commercial team will review the application and contact you to confirm scope, technical requirements, and a useful next conversation. The preferred window is not a confirmed appointment.",
      });
      resetTurnstile();
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.code === "submission_unconfirmed"
          ? error.message
          : `${error instanceof Error ? error.message : "We could not submit your request."} Try again, or call 289-748-9666.`,
      });
      resetTurnstile();
      try {
        TrackingEvents.trackFormStep("submit_error", { ...trackingDimensions, error_type: "submission_failed" });
      } catch (trackingError) {
        console.error("Submission error tracking failed:", trackingError);
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  if (status.type === "success") {
    return (
      <div className="rounded-[1.375rem] border border-emerald-200 bg-emerald-50 p-6 sm:p-8" role="status" aria-live="polite">
        <CheckCircleIcon className="h-9 w-9 text-water-teal" aria-hidden="true" />
        <h2 className="mt-4 text-2xl font-semibold text-water-ink">Project request received</h2>
        <p className="mt-3 max-w-2xl text-base leading-7 text-water-muted">{status.message}</p>
        <a href="tel:+12897489666" className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-water-blue hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2">
          <PhoneIcon className="h-5 w-5" aria-hidden="true" />
          Call 289-748-9666
        </a>
      </div>
    );
  }

  return (
    <>
      <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer strategy="afterInteractive" onLoad={handleScriptLoad} />
      <form ref={formRef} onSubmit={handleSubmit} onFocusCapture={trackStart} className="wd-form space-y-6" noValidate>
        <p className="text-sm leading-6 text-water-muted">Company, first name, phone, and application are required. Everything else helps us prepare a better technical follow-up.</p>

        <div>
          <FieldLabel htmlFor={`${id}-company`}>Company name</FieldLabel>
          <input id={`${id}-company`} name="companyName" autoComplete="organization" value={formData.companyName} onChange={handleChange} required aria-invalid={Boolean(errors.companyName)} aria-describedby={errors.companyName ? `${id}-company-error` : undefined} className={fieldClassName} />
          <InlineError id={`${id}-company-error`}>{errors.companyName}</InlineError>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor={`${id}-first-name`}>First name</FieldLabel>
            <input id={`${id}-first-name`} name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? `${id}-first-name-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-first-name-error`}>{errors.firstName}</InlineError>
          </div>
          <div>
            <FieldLabel htmlFor={`${id}-last-name`} optional>Last name</FieldLabel>
            <input id={`${id}-last-name`} name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} className={fieldClassName} />
          </div>
          <div>
            <FieldLabel htmlFor={`${id}-phone`}>Phone</FieldLabel>
            <input id={`${id}-phone`} name="phone" type="tel" inputMode="tel" autoComplete="tel" value={formData.phone} onChange={handleChange} placeholder="289-748-9666" required aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? `${id}-phone-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-phone-error`}>{errors.phone}</InlineError>
          </div>
          <div>
            <FieldLabel htmlFor={`${id}-email`} optional>Email</FieldLabel>
            <input id={`${id}-email`} name="email" type="email" inputMode="email" autoComplete="email" value={formData.email} onChange={handleChange} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? `${id}-email-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-email-error`}>{errors.email}</InlineError>
          </div>
        </div>

        <div>
          <FieldLabel htmlFor={`${id}-application`}>Main application</FieldLabel>
          <select id={`${id}-application`} name="application" value={formData.application} onChange={handleChange} required aria-invalid={Boolean(errors.application)} aria-describedby={errors.application ? `${id}-application-error` : undefined} className={fieldClassName}>
            {APPLICATIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <InlineError id={`${id}-application-error`}>{errors.application}</InlineError>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor={`${id}-facility`} optional>Facility type</FieldLabel>
            <select id={`${id}-facility`} name="facilityType" value={formData.facilityType} onChange={handleChange} className={fieldClassName}>
              {FACILITY_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div>
            <FieldLabel htmlFor={`${id}-flow`} optional>Target flow or demand</FieldLabel>
            <input id={`${id}-flow`} name="targetFlow" value={formData.targetFlow} onChange={handleChange} placeholder="e.g. peak L/min, fixtures, shifts" className={fieldClassName} />
          </div>
        </div>

        <div>
          <FieldLabel htmlFor={`${id}-details`} optional>Project or system details</FieldLabel>
          <textarea id={`${id}-details`} name="projectDetails" rows={4} value={formData.projectDetails} onChange={handleChange} placeholder="Current problem, equipment, process requirements, constraints, or desired outcome…" className={fieldClassName} />
        </div>

        <div className="relative">
          <FieldLabel htmlFor={`${id}-address`} optional>Facility address</FieldLabel>
          <input
            ref={addressInputRef}
            id={`${id}-address`}
            name="address"
            value={formData.address}
            onChange={handleAddressChange}
            onKeyDown={handleAddressKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            autoComplete="street-address"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            aria-controls={`${id}-address-listbox`}
            aria-activedescendant={showSuggestions && activeSuggestion >= 0 ? `${id}-address-option-${activeSuggestion}` : undefined}
            placeholder="Start typing a Canadian address…"
            className={fieldClassName}
          />
          {showSuggestions ? (
            <div ref={suggestionsRef} className="absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-[0.875rem] border border-water-border bg-white p-1 shadow-xl">
              {isLoadingSuggestions ? <p className="px-3 py-2 text-sm text-water-muted" role="status">Looking up addresses…</p> : null}
              {!isLoadingSuggestions && suggestions.length === 0 ? <p className="px-3 py-2 text-sm text-water-muted">No suggestions. You can keep the address as typed.</p> : null}
              <ul id={`${id}-address-listbox`} role="listbox">
                {suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.id}
                    id={`${id}-address-option-${index}`}
                    role="option"
                    aria-selected={activeSuggestion === index}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectSuggestion(suggestion)}
                    className={`w-full cursor-pointer rounded-[0.625rem] px-3 py-2 text-left text-sm leading-6 text-water-ink ${activeSuggestion === index ? "bg-sky-50" : "hover:bg-water-canvas"}`}
                  >
                    {suggestion.properties?.full_address || suggestion.place_name || suggestion.properties?.name}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div>
          <FieldLabel htmlFor={`${id}-window`} optional>Preferred consultation window</FieldLabel>
          <input id={`${id}-window`} name="consultationWindow" type="datetime-local" value={formData.consultationWindow} onChange={handleChange} className={fieldClassName} />
          <p className="mt-1 text-sm leading-6 text-water-muted">This is a preference, not a confirmed appointment. We will contact you first.</p>
        </div>

        <div>
          <div ref={turnstileRef} className="min-h-[66px]" aria-label="Human verification" />
          <InlineError id={`${id}-turnstile-error`}>{turnstileError}</InlineError>
        </div>

        {status.type === "error" ? <p className="rounded-[0.875rem] border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800" role="alert">{status.message}</p> : null}

        <button type="submit" disabled={isSubmitting} className="inline-flex min-h-12 w-full items-center justify-center rounded-[0.875rem] border border-water-blue bg-water-blue px-6 py-3 text-base font-semibold text-white transition-[background-color,border-color] duration-200 hover:border-water-blue-dark hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70 sm:w-auto">
          {isSubmitting ? "Sending request…" : "Discuss My Water Project"}
        </button>
        <p className="text-sm leading-6 text-water-muted">No obligation. We use these details only to prepare and respond to your request.</p>
      </form>
    </>
  );
}
